import { IGetResidentRes } from '@/types/api/resident/getResident';
import { IGetResidentActiveRes } from '@/types/api/resident/getResidentActive';

export const RESIDENT_CATEGORIES_STORAGE_KEY = 'residentCategories';

export const getCategoryUnitIds = (categories: string[]) =>
  categories.filter((value) => /^unit-[1-9]\d*$/.test(value)).map((value) => Number(value.slice(5)));

// 各画面で状態の定義を維持しつつ、複数状態のOR判定を共有する。
export const matchesResidentStatus = (
  categories: string[],
  deviceAssigned: boolean,
  deviceAttached: boolean,
) => {
  const assigned = categories.includes('deviceAssigned');
  const attached = categories.includes('deviceAttached');
  return (!assigned && !attached) || (assigned && deviceAssigned) || (attached && deviceAttached);
};

export interface ResidentFilterData {
  nursingHomeUnitId: number | null;
  deviceAssigned: boolean;
  deviceAttached: boolean;
}

export interface WithResidentFilterData {
  filterData?: ResidentFilterData;
}

// 保持した全件から表示対象を選ぶ。ユニット専用アカウントの取得範囲はAPI側で固定する。
export const filterResidentList = <T extends WithResidentFilterData>(
  residents: T[], categories: string[], permittedUnitId: number | null = null,
): T[] => {
  // ユニット権限では、以前保存したユニット選択があっても自身のユニットを表示する。
  const unitIds = permittedUnitId === null ? getCategoryUnitIds(categories) : [];
  return residents.filter(({ filterData }) =>
    (!unitIds.length || (filterData?.nursingHomeUnitId != null && unitIds.includes(filterData.nursingHomeUnitId)))
    && matchesResidentStatus(categories, filterData?.deviceAssigned ?? false, filterData?.deviceAttached ?? false));
};

export const filterResidentsByCategories = (
  residents: IGetResidentRes[],
  activeResidents: IGetResidentActiveRes[],
  categories: string[],
  permittedUnitId: number | null,
) => {
  // ユニット権限では、以前保存したユニット選択があっても自身のユニットを表示する。
  const unitIds = permittedUnitId === null ? getCategoryUnitIds(categories) : [];
  const statuses = categories.filter((value) => value === 'deviceAssigned' || value === 'deviceAttached');
  const activeById = new Map(activeResidents.map((resident) => [resident.id, resident]));

  return residents.filter((resident) => {
    if (permittedUnitId !== null && resident.nursingHomeUnitId !== permittedUnitId) return false;
    if (unitIds.length && !unitIds.includes(resident.nursingHomeUnitId ?? -1)) return false;
    if (!statuses.length) return true;
    const active = activeById.get(resident.id);
    if (!active) return false;
    // 状態同士・ユニット同士はOR、状態とユニットの組み合わせはAND。
    return matchesResidentStatus(categories,
      active.serialNumber != null || active.gasSerialNumber != null,
      active.urinePercentage != null || active.gasPercentage != null);
  });
};

export const readResidentCategories = (): string[] => {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(RESIDENT_CATEGORIES_STORAGE_KEY) ?? 'null');
    if (Array.isArray(saved)) {
      return Array.from(new Set(saved.filter((value): value is string => typeof value === 'string' && (
        value === 'deviceAssigned' || value === 'deviceAttached' || /^unit-[1-9]\d*$/.test(value)
      ))));
    }
    const condition = localStorage.getItem('condition');
    if (condition === 'deviceAssigned' || condition === 'deviceAttached') return [condition];
    const unitId = Number(localStorage.getItem('unit'));
    return condition === 'unit' && Number.isSafeInteger(unitId) && unitId > 0 ? [`unit-${unitId}`] : [];
  } catch {
    return [];
  }
};

export const saveResidentCategories = (categories: string[]) => {
  try {
    localStorage.setItem(RESIDENT_CATEGORIES_STORAGE_KEY, JSON.stringify(categories));
  } catch {
    // 保存が使えなくても画面内の絞り込みは続けられる。
  }
};
